import EditableText from "../ui/EditableText"

const defaultTechnicalFields = [
  "Physics",
  "Organic chemistry",
  "Mechanical engineering",
  "Semiconductor technology",
  "Medical technology",
  "Laser technology",
  "Automotive technology",
  "Energy technology",
  "Materials technology",
  "Information and communication technology",
  "Software",
  "Consumer goods",
]

export interface ActivityAreaInput {
  title: string
  text: string
}

const defaultAreasOfActivity: ActivityAreaInput[] = [
  {
    title: "Strategic IP management",
    text: "Strategic management consulting and development of IP strategies aligned with business objectives.",
  },
  {
    title: "IP portfolio development",
    text: "Strategic development and management of patent, trademark and design portfolios.",
  },
  {
    title: "IP generation",
    text: "Drafting and filing patents, utility models, trademarks and designs.",
  },
  {
    title: "Prosecution",
    text: "Conducting proceedings for the grant of industrial property rights.",
  },
  {
    title: "Opposition & appeal",
    text: "Defending IP rights in opposition, nullity and appeal proceedings.",
  },
  {
    title: "Enforcement",
    text: "Enforcement of IP rights in and out of court, including patent and trademark infringement matters.",
  },
  {
    title: "Technology transfer",
    text: "Transfer of intellectual property rights and technologies.",
  },
  {
    title: "Licensing",
    text: "Licensing law and licensing agreements.",
  },
  {
    title: "IP valuation",
    text: "Valuation of intellectual property portfolios.",
  },
  {
    title: "Freedom to operate",
    text: "Freedom-to-operate analyses.",
  },
  {
    title: "Due diligence",
    text: "Preparation and execution of IP due diligence.",
  },
]

interface AboutExpertiseSectionProps {
  heading1?: string
  intro?: string
  technicalFields?: string[]
  heading2?: string
  areasOfActivity?: ActivityAreaInput[]
  editable?: boolean
  onSaveHeading1?: (value: string) => void
  onSaveIntro?: (value: string) => void
  onSaveField?: (index: number, value: string) => void
  onSaveHeading2?: (value: string) => void
  onSaveAreaTitle?: (index: number, value: string) => void
  onSaveAreaText?: (index: number, value: string) => void
}

export default function AboutExpertiseSection({
  heading1 = "TECHNICAL EXPERIENCE",
  intro = "Dr. Dantz has worked successfully in a broad range of technical fields, including:",
  technicalFields = defaultTechnicalFields,
  heading2 = "MAIN AREAS OF ACTIVITY",
  areasOfActivity = defaultAreasOfActivity,
  editable = false,
  onSaveHeading1,
  onSaveIntro,
  onSaveField,
  onSaveHeading2,
  onSaveAreaTitle,
  onSaveAreaText,
}: AboutExpertiseSectionProps) {
  return (
    <section className="bg-white px-4 py-16 md:px-8">
      <div className="mx-auto max-w-6xl text-dantzBlue">
        <div className="mx-auto w-11/12 border-t border-dantzBlue" />

        <EditableText
          as="h2"
          value={heading1}
          editMode={editable}
          onSave={(value) => onSaveHeading1?.(value)}
          className="mb-8 mt-12 block text-center text-[26px] font-light tracking-widest"
        />

        <EditableText
          as="p"
          value={intro}
          editMode={editable}
          multiline
          onSave={(value) => onSaveIntro?.(value)}
          className="mx-auto block max-w-3xl text-center text-sm leading-relaxed text-gray-600"
        />

        <ul className="mx-auto mt-6 grid max-w-3xl grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2 md:grid-cols-3">
          {technicalFields.map((field, index) => (
            <li key={index} className="flex items-start gap-2">
              <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-dantzBlue" />
              <EditableText
                as="span"
                value={field}
                editMode={editable}
                onSave={(value) => onSaveField?.(index, value)}
              />
            </li>
          ))}
        </ul>

        <EditableText
          as="h2"
          value={heading2}
          editMode={editable}
          onSave={(value) => onSaveHeading2?.(value)}
          className="mb-10 mt-20 block text-center text-[26px] font-light tracking-widest"
        />

        <div className="grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-2">
          {areasOfActivity.map((area, index) => (
            <div key={index}>
              <EditableText
                as="h3"
                value={area.title}
                editMode={editable}
                onSave={(value) => onSaveAreaTitle?.(index, value)}
                className="block text-base font-semibold tracking-wide"
              />
              <EditableText
                as="p"
                value={area.text}
                editMode={editable}
                multiline
                onSave={(value) => onSaveAreaText?.(index, value)}
                className="mt-1 block text-sm leading-relaxed text-gray-600"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
